import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

const BASE_URL = process.env.REACT_APP_ENDPOINT;

/**
 * Hook to fetch monthly curtailment data for a specific permit and year
 */
export const useMonthlyCurtailmentData = (permitId, year, options = {}) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery(
    ["monthly-curtailment", permitId, year],
    async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${BASE_URL}/api/drought-production/monthly-curtailment/${permitId}/${year}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      enabled: !!(permitId && year),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      ...options,
    }
  );
};

/**
 * Hook to fetch year summary data for a specific permit and year
 */
export const useYearSummaryData = (permitId, year, options = {}) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery(
    ["year-summary", permitId, year],
    async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${BASE_URL}/api/drought-production/year-summary/${permitId}/${year}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      enabled: !!(permitId && year),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      ...options,
    }
  );
};

/**
 * Hook to fetch available years for a specific permit
 */
export const useAvailableYears = (permitId, options = {}) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery(
    ["available-years", permitId],
    async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${BASE_URL}/api/drought-production/available-years/${permitId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      enabled: !!permitId,
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: 60 * 60 * 1000, // 1 hour
      ...options,
    }
  );
};

/**
 * Combined hook that fetches all drought production data for a permit and year
 */
export const useDroughtProductionData = (permitId, year) => {
  const monthlyQuery = useMonthlyCurtailmentData(permitId, year);
  const yearSummaryQuery = useYearSummaryData(permitId, year);
  const availableYearsQuery = useAvailableYears(permitId);

  return {
    monthlyData: monthlyQuery.data,
    yearSummary: yearSummaryQuery.data,
    availableYears: availableYearsQuery.data,
    isLoading:
      monthlyQuery.isLoading ||
      yearSummaryQuery.isLoading ||
      availableYearsQuery.isLoading,
    isError:
      monthlyQuery.isError ||
      yearSummaryQuery.isError ||
      availableYearsQuery.isError,
    error:
      monthlyQuery.error || yearSummaryQuery.error || availableYearsQuery.error,
  };
};
